import React, { useEffect, useState } from 'react';
import { Check, Loader2, SlidersHorizontal, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  suggestBuyerPreferencesFromNotes,
  updateContactBuyerPreferences,
} from '../../../api/contact';

/**
 * "Found in this note" box shown under a buyer's notes once a note is saved.
 *
 * Christian Gravias, Monday 13391227323: "Update buyer preferences based on
 * notes". The note on his screenshot said "looking to spend max around $1.2m"
 * while the buyer's Preferences had no budget at all. The backend reads the note
 * and returns only what would change; the agent ticks what is right and applies
 * it. Nothing is written without that click, because a note is easy to misread
 * ("Renting in Fairfield" is where they live, not where they want to buy).
 *
 * Renders nothing while there is nothing to suggest, when the AI is not set up,
 * or on any error: the note itself is already saved, so this must never get in
 * the way of it.
 *
 * Props:
 *   contactId  the buyer's Contact id
 *   noteText   the note that was just saved
 *   onApplied  called with the rebuilt buyer_preferences snapshot after Apply
 */
const NotePreferenceSuggestions = ({ contactId, noteText, onApplied }) => {
  const [state, setState] = useState('loading'); // loading | ready | applying | applied | hidden
  const [changes, setChanges] = useState([]);
  const [picked, setPicked] = useState({});

  useEffect(() => {
    let active = true;
    setState('loading');
    (async () => {
      try {
        const res = await suggestBuyerPreferencesFromNotes(contactId, { text: noteText });
        if (!active) return;
        const found = res?.data?.changes || [];
        setChanges(found);
        setPicked(Object.fromEntries(found.map((c) => [c.field, true])));
        setState(found.length ? 'ready' : 'hidden');
      } catch (e) {
        if (active) setState('hidden');
      }
    })();
    return () => { active = false; };
  }, [contactId, noteText]);

  if (state === 'hidden' || !contactId) return null;

  if (state === 'loading') {
    return (
      <div className="mt-3 flex items-center gap-2 text-[11px] text-gray-400" data-testid="note-prefs-checking">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking this note for buying preferences…
      </div>
    );
  }

  const chosen = changes.filter((c) => picked[c.field]);

  if (state === 'applied') {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800">
        <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Preferences updated: {chosen.map((c) => `${c.label} ${c.suggested.replace(/^\+ /, '')}`).join(', ')}
        </span>
      </div>
    );
  }

  const apply = async () => {
    if (!chosen.length) return;
    setState('applying');
    try {
      const payload = Object.fromEntries(chosen.map((c) => [c.field, c.value]));
      const res = await updateContactBuyerPreferences(contactId, payload);
      setState('applied');
      onApplied?.(res?.data?.buyer_preferences);
    } catch (e) {
      setState('ready');
      toast.error(e?.response?.data?.detail || "Couldn't update this buyer's preferences");
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-[#B7E9EE] bg-[#F0FDFE] p-3" data-testid="note-prefs-suggestions">
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-bold text-[#00838F]">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Found in this note
        </p>
        <button
          type="button"
          onClick={() => setState('hidden')}
          title="Dismiss"
          className="rounded p-0.5 text-gray-400 hover:bg-white hover:text-gray-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <ul className="mt-2 space-y-1.5">
        {changes.map((c) => (
          <li key={c.field}>
            <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={!!picked[c.field]}
                onChange={() => setPicked((p) => ({ ...p, [c.field]: !p[c.field] }))}
                className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]"
              />
              <span className="min-w-0">
                <span className="font-semibold text-gray-900">{c.label}</span>{' '}
                <span className="text-gray-400 line-through decoration-gray-300">{c.current}</span>{' '}
                <span className="text-gray-400">→</span>{' '}
                <span className="font-semibold text-[#00838F]">{c.suggested}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={apply}
          disabled={!chosen.length || state === 'applying'}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#55C5D0] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#45B5C0] disabled:opacity-50"
        >
          {state === 'applying' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Apply to preferences
        </button>
        <button
          type="button"
          onClick={() => setState('hidden')}
          className="text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default NotePreferenceSuggestions;
